var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { ArrayInputContext, FieldTitle, InputHelperText, Labeled, LinearProgress, isRequired, ResourceContextProvider, useResourceContext, useTranslate, useSourceContext, SourceContextProvider, } from 'react-admin';
import clsx from 'clsx';
import { InputLabel, FormControl, FormHelperText, Typography, styled, } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useReferenceManyInputController, } from './useReferenceManyInputController';
/**
 * Use `<ReferenceManyInput>` in an `<Edit>` or `<Create>` view to edit one-to-many relationships,
 * e.g. to edit the variants of a product in the product edition view.
 *
 * @example
 * import {
 *     Edit,
 *     SimpleForm,
 *     TextInput,
 *     NumberInput,
 *     ReferenceInput,
 *     SelectInput,
 * } from 'react-admin';
 * import { ReferenceManyInput } from '@react-admin/ra-relationships';
 *
 * const ProductEdit = () => (
 *     <Edit mutationMode="optimistic">
 *         <SimpleForm>
 *             <TextInput source="name" />
 *             <NumberInput source="price" />
 *             <ReferenceInput source="category_id" reference="categories" />
 *             <ReferenceManyInput reference="variants" target="product_id">
 *                 <SimpleFormIterator inline>
 *                     <TextInput source="sku" />
 *                     <SelectInput source="size" choices={sizes} />
 *                     <SelectInput source="color" choices={colors} />
 *                     <NumberInput source="stock" defaultValue={0} />
 *                 </SimpleFormIterator>
 *             </ReferenceManyInput>
 *         </SimpleForm>
 *     </Edit>
 * );
 */
export var ReferenceManyInput = function (_a) {
    var _b;
    var margin = _a.margin, variant = _a.variant, disabled = _a.disabled, children = _a.children, className = _a.className, helperText = _a.helperText, label = _a.label, sx = _a.sx, props = __rest(_a, ["margin", "variant", "disabled", "children", "className", "helperText", "label", "sx"]);
    var resource = useResourceContext(props);
    var _c = useReferenceManyInputController(props), fieldArray = _c.fieldArray, fetchError = _c.error, isPending = _c.isPending, temporaryFieldName = _c.temporaryFieldName;
    var _d = useFormContext(), getFieldState = _d.getFieldState, formState = _d.formState;
    var _e = getFieldState(temporaryFieldName, formState), invalid = _e.invalid, error = _e.error;
    var translate = useTranslate();
    var parentSourceContext = useSourceContext();
    // The SourceContext will be read by children of ArrayInput to compute their composed source and label
    //
    // <ArrayInput source="orders" /> => SourceContext is "orders"
    //   <SimpleFormIterator> => SourceContext is "orders.0"
    //     <DateInput source="date" /> => final source for this input will be "orders.0.date"
    //   </SimpleFormIterator>
    // </ArrayInput>
    //
    var sourceContext = React.useMemo(function () { return ({
        // source is the source of the ArrayInput child
        getSource: function (source) {
            if (!source) {
                // SimpleFormIterator calls getSource('') to get the arraySource
                return parentSourceContext.getSource(temporaryFieldName);
            }
            // We want to support nesting and composition with other inputs (e.g. TranslatableInputs, ReferenceOneInput, etc),
            // we must also take into account the parent SourceContext
            //
            // <ArrayInput source="orders" /> => SourceContext is "orders"
            //   <SimpleFormIterator> => SourceContext is "orders.0"
            //      <DateInput source="date" /> => final source for this input will be "orders.0.date"
            //      <ArrayInput source="items" /> => SourceContext is "orders.0.items"
            //          <SimpleFormIterator> => SourceContext is "orders.0.items.0"
            //              <TextInput source="reference" /> => final source for this input will be "orders.0.items.0.reference"
            //          </SimpleFormIterator>
            //      </ArrayInput>
            //   </SimpleFormIterator>
            // </ArrayInput>
            return parentSourceContext.getSource("".concat(temporaryFieldName, ".").concat(source));
        },
        // if Array source is items, and child source is name, .0.name => resources.orders.fields.items.name
        getLabel: function (source) {
            return "resources.".concat(props.reference, ".fields.").concat(source);
        },
    }); }, [parentSourceContext, props.reference, temporaryFieldName]);
    if (isPending) {
        return (_jsx(Root, { fullWidth: true, margin: margin, sx: sx, "data-testid": "loading", children: _jsx(Labeled, { label: label, source: props.reference, resource: resource, children: _jsx(LinearProgress, {}) }) }));
    }
    if (fetchError) {
        return (_jsx(Root, { fullWidth: true, margin: margin, sx: sx, "data-testid": "error", children: _jsx(Labeled, { label: label, source: props.reference, resource: resource, children: _jsx(Typography, { sx: { color: 'error.main' }, children: translate('ra-relationships.referenceManyInput.fetchError', {
                        _: 'Error fetching references',
                    }) }) }) }));
    }
    return (_jsx(ResourceContextProvider, { value: props.reference, children: _jsxs(Root, { fullWidth: true, margin: margin, className: clsx('ra-input', "ra-input-".concat(temporaryFieldName), className), error: invalid, sx: sx, children: [_jsx(InputLabel, { component: "span", className: ReferenceManyInputClasses.label, shrink: true, error: invalid, children: _jsx(FieldTitle, { label: label, source: props.reference, resource: resource, isRequired: isRequired(props.validate) }) }), _jsx(ArrayInputContext.Provider, { value: fieldArray, children: _jsx(SourceContextProvider, { value: sourceContext, children: children }) }), invalid || helperText ? (_jsx(FormHelperText, { error: invalid, children: _jsx(InputHelperText, { error: (_b = error === null || error === void 0 ? void 0 : error.root) === null || _b === void 0 ? void 0 : _b.message, helperText: helperText }) })) : null] }) }));
};
var PREFIX = 'RaReferenceManyInput';
export var ReferenceManyInputClasses = {
    label: "".concat(PREFIX, "-label"),
};
var Root = styled(FormControl, {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {},
        _b["& .".concat(ReferenceManyInputClasses.label)] = {
            position: 'relative',
            left: theme.spacing(-1.5),
        },
        _b);
});
